import { Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString, SecondaryButton } from "common";
import { FormikHelpers } from "formik";
import React, { useState } from "react";
import { VariantGroup, VariantOption } from "types";
import { array, object } from "yup";
import { VariantOptionsSection } from "./VariantOptionsSection";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { useModal } from "@/hooks/useVisibility";

export const VARIANT_COMBINATIONS_WARNING_THRESHOLD = 1000;

export function calculateCombinations(
    variantGroups: VariantGroup[],
    newGroup: VariantGroup,
    existingGroup?: VariantGroup,
): number {
    const groups = existingGroup
        ? variantGroups.map((group) => (group === existingGroup ? newGroup : group))
        : [...variantGroups, newGroup];
    return groups.reduce((product, g) => product * Math.max(g.options.length, 1), 1);
}

interface LargeVariantCombinationsWarningModalProps {
    combinationCount: number;
    onConfirm: () => void;
    onCancel: () => void;
}

const LargeVariantCombinationsWarningModal = ({
    combinationCount,
    onConfirm,
    onCancel,
}: LargeVariantCombinationsWarningModalProps) => {
    return (
        <Modal isOpen size="small" onClose={onCancel}>
            <Modal.Header>Large number of variants</Modal.Header>
            <Modal.Body>
                This will create{" "}
                <strong>
                    <var data-var="combinationCount">{combinationCount}</var> combinations
                </strong>
                . Creating this many variants will take a long time. Are you sure you haven&apos;t made a mistake?
            </Modal.Body>
            <Modal.Footer>
                <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
                <SubmitButton onClick={onConfirm}>Yes, create variants</SubmitButton>
            </Modal.Footer>
        </Modal>
    );
};

interface EditVariantGroupFormValues {
    name: string;
    options: VariantOption[];
}

interface EditVariantGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (group: VariantGroup) => void;
    group?: VariantGroup;
    variantGroups: VariantGroup[];
}

export const EditVariantGroupModal = ({
    isOpen,
    onClose,
    onSave,
    group,
    variantGroups,
}: EditVariantGroupModalProps) => {
    return (
        <Modal isOpen={isOpen} size="medium" onClose={onClose}>
            <EditVariantGroupModalBody group={group} variantGroups={variantGroups} onClose={onClose} onSave={onSave} />
        </Modal>
    );
};

interface EditVariantGroupModalBodyProps {
    onClose: () => void;
    onSave: (group: VariantGroup) => void;
    group?: VariantGroup;
    variantGroups: VariantGroup[];
}

const EditVariantGroupModalBody = ({
    onClose,
    onSave,
    group: existingGroup,
    variantGroups,
}: EditVariantGroupModalBodyProps) => {
    const isEditing = Boolean(existingGroup);
    const warningModal = useModal();
    const [pendingGroup, setPendingGroup] = useState<VariantGroup | null>(null);
    const [warningCombinationCount, setWarningCombinationCount] = useState(0);

    const initialValues: EditVariantGroupFormValues = {
        name: existingGroup?.name ?? "",
        options: existingGroup?.options ?? [],
    };

    const validationSchema = object().shape({
        name: requiredString().test("unique-name", "name already exists", (value) => {
            if (!value) return true;
            const normalized = value.trim().toLowerCase();
            return !variantGroups.some(
                (group) => group !== existingGroup && group.name.trim().toLowerCase() === normalized,
            );
        }),
        options: array().ensure().min(1, "At least one option is required"),
    });

    const handleSubmit = (
        values: EditVariantGroupFormValues,
        { setSubmitting }: FormikHelpers<EditVariantGroupFormValues>,
    ) => {
        const group: VariantGroup = {
            id: existingGroup?.id,
            name: values.name.trim(),
            options: values.options,
        };

        const combinations = calculateCombinations(variantGroups, group, existingGroup);
        if (combinations >= VARIANT_COMBINATIONS_WARNING_THRESHOLD) {
            setPendingGroup(group);
            setWarningCombinationCount(combinations);
            warningModal.show();
            setSubmitting(false);
            return;
        }

        onSave(group);
        onClose();
    };

    const handleWarningConfirm = () => {
        if (pendingGroup) {
            onSave(pendingGroup);
            onClose();
        }
        warningModal.hide();
    };

    const handleWarningCancel = () => {
        warningModal.hide();
        setPendingGroup(null);
        setWarningCombinationCount(0);
    };

    return (
        <>
            <XolaFormik
                initialValues={initialValues}
                validateOnChange={false}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, isSubmitting }) => (
                    <>
                        <Modal.Header>{isEditing ? "Edit Variant Group" : "Add Variant Group"}</Modal.Header>
                        <Modal.Body className="space-y-4">
                            <FormField
                                name="name"
                                label="Name"
                                tooltip='The name or category of variant (e.g. "Size" or "Color")'
                            >
                                <InputField isRequired placeholder="Name" />
                            </FormField>

                            <VariantOptionsSection />
                        </Modal.Body>
                        <Modal.Footer>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                                Done
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                )}
            </XolaFormik>

            {warningModal.isVisible && (
                <LargeVariantCombinationsWarningModal
                    combinationCount={warningCombinationCount}
                    onConfirm={handleWarningConfirm}
                    onCancel={handleWarningCancel}
                />
            )}
        </>
    );
};
